if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector('vo-cart-notification') ||
          document.querySelector('[data-vo-cart-notification]') ||
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.isSubmitting = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isSubmitting) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const ctaState = this.submitButton.dataset.voCtaState;
        if (ctaState && ctaState !== 'add-to-bag') {
          if (ctaState === 'view-alternatives') {
            const alternativesUrl = this.submitButton.dataset.voAlternativesUrl || '/collections/all';
            window.location.assign(alternativesUrl);
          }
          return;
        }

        this.isSubmitting = true;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantIdToUse = formData.get('id');
        const requestedQuantity = this.getRequestedQuantity(formData);
        const config = fetchConfig('json');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (requestedQuantity === 1) {
          delete config.headers['Content-Type'];
          config.body = this.buildSingleLineAddPayload(formData, variantIdToUse);
        } else {
          config.body = JSON.stringify(this.buildSplitLineAddPayload(formData, variantIdToUse));
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            const productVariantId = this.getAddedProductVariantId(response, variantIdToUse);

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.isSubmitting = false;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButtonText) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            this.submitButton.dataset.voAddLabel || window.variantStrings.addToBag || window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      buildSingleLineAddPayload(formData, variantId) {
        formData.set('id', variantId);
        formData.set('quantity', '1');
        formData.set('properties[_added_at]', this.createCartLineMarker(0));

        if (this.cart) {
          formData.set('sections', this.cart.getSectionsToRender().map((section) => section.id).join(','));
          formData.set('sections_url', window.location.pathname);
        }

        return formData;
      }

      buildSplitLineAddPayload(formData, variantId) {
        const requestedQuantity = this.getRequestedQuantity(formData);
        const baseProperties = this.getLineItemProperties(formData);
        const sellingPlan = formData.get('selling_plan');
        const items = [];

        for (let index = 0; index < requestedQuantity; index += 1) {
          const item = {
            id: variantId,
            quantity: 1,
            properties: {
              ...baseProperties,
              _added_at: this.createCartLineMarker(index),
            },
          };

          if (sellingPlan) item.selling_plan = sellingPlan;
          items.push(item);
        }

        const payload = {
          items,
          sections_url: window.location.pathname,
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
        }

        return payload;
      }

      getRequestedQuantity(formData) {
        const quantity = Number.parseInt(formData.get('quantity'), 10);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      getLineItemProperties(formData) {
        const properties = {};
        formData.forEach((value, key) => {
          const match = /^properties\[(.+)]$/.exec(key);
          if (!match || value === null || value === undefined || value === '') return;
          properties[match[1]] = value;
        });
        return properties;
      }

      createCartLineMarker(index) {
        return `${new Date().toISOString()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
      }

      getAddedProductVariantId(response, fallback) {
        const firstItem = Array.isArray(response?.items) ? response.items[0] : response;
        return firstItem?.variant_id || firstItem?.id || fallback;
      }
    }
  );
}
